import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  CameraVideo,
  CheckLg,
  ChevronDown,
  ChevronUp,
  Files,
  FileEarmarkExcel,
  FileEarmarkImage,
  FileEarmarkMusic,
  FileEarmarkPdf,
  FileEarmarkPpt,
} from 'react-bootstrap-icons'
import { CampaignContext } from '../../../../../contexts/CampaignContext'

const typeOptions = [
  { label: 'All types', description: 'Show every uploaded file', value: null, icon: Files },
  { label: 'Images', description: 'PNG, JPG, GIF, SVG', value: 'image', icon: FileEarmarkImage },
  { label: 'PDFs', description: 'PDF documents', value: 'pdf', icon: FileEarmarkPdf },
  { label: 'Videos', description: 'MOV, MP4, AVI', value: 'video', icon: CameraVideo },
  { label: 'Spreadsheets', description: 'Excel files', value: 'stylesheet', icon: FileEarmarkExcel },
  { label: 'Presentations', description: 'Slide decks', value: 'ppt', icon: FileEarmarkPpt },
  { label: 'Audio', description: 'MP3, FLAC, WAV', value: 'audio', icon: FileEarmarkMusic },
]

function TypeOption({ option, isSelected, onSelect }) {
  const Icon = option.icon

  return (
    <button
      type="button"
      className={`w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:hover:bg-rb-dark-mode-lighter dark:focus:bg-rb-dark-mode-lighter ${isSelected ? 'text-rb-green' : 'text-gray-800 dark:text-gray-200'}`}
      onClick={() => onSelect(option)}
      role="option"
      aria-selected={isSelected}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`shrink-0 rounded-md p-2 ${isSelected ? 'bg-rb-green/10 text-rb-green' : 'bg-gray-100 text-gray-500 dark:bg-rb-dark-mode-lighter dark:text-gray-300'}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-sm truncate">{option.label}</span>
          <span className="block text-xs text-gray-400 dark:text-gray-500 truncate">{option.description}</span>
        </span>
      </div>
      {isSelected && <CheckLg className="shrink-0 text-rb-green" />}
    </button>
  )
}

export default observer(function TypeSearch() {
  const { documentSearch } = useContext(CampaignContext)
  const dropdownRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const selectedType = typeOptions.find(option => option.value === documentSearch.type) || typeOptions[0]
  const SelectedIcon = selectedType.icon

  useEffect(() => {
    const closeDropdown = event => {
      if (!dropdownRef.current?.contains(event.target)) setIsOpen(false)
    }

    const closeOnEscape = event => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', closeDropdown)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('mousedown', closeDropdown)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  const setType = (option) => {
    documentSearch.type = option?.value || null
    setIsOpen(false)
  }

  return (
    <div className="relative w-64" ref={dropdownRef}>
      <button
        type="button"
        aria-label="Filter files by type"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`h-[54px] w-full rounded-xl border-1 border-gray-200 dark:border-rb-dark-mode-lighter bg-gray-100 dark:bg-rb-dark-mode-lighter p-1 text-left transition-colors hover:border-rb-green focus:outline-none focus:border-rb-green ${isOpen ? 'border-rb-green' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="flex h-full items-center justify-between gap-3">
          <span className="flex items-center gap-3 min-w-0">
            <span className="h-11 w-11 rounded-xl bg-white dark:bg-rb-dark-mode dark:text-white flex items-center justify-center shrink-0">
              <SelectedIcon className="w-4 h-4 text-rb-green" />
            </span>
            <span className="truncate font-semibold text-gray-800 dark:text-white">{selectedType.label}</span>
          </span>
          {isOpen
            ? <ChevronUp className="text-gray-500 dark:text-gray-400 shrink-0 mr-2" />
            : <ChevronDown className="text-gray-500 dark:text-gray-400 shrink-0 mr-2" />}
        </span>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border-1 border-gray-200 dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode shadow-lg">
          <div className="max-h-56 overflow-y-auto p-2" role="listbox" aria-label="File type options">
            {typeOptions.map(option => (
              <TypeOption
                key={option.label}
                option={option}
                isSelected={option.value === selectedType.value}
                onSelect={setType}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
})